import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, getImageUrl } from '@/lib/api'
import toast from 'react-hot-toast'
import { PlusIcon, PencilIcon, TrashIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface Level {
  _id: string
  number: number
  title: string
}

interface Lesson {
  _id: string
  levelId: Level
  type: string
  title: string
  description: string
  image: string
  content: string
  xpReward: number
  skillCategory: string
  skillCategories?: string[]
  skillProgress: number
  order: number
  pointsReward: number
  durationMinutes: number
  isActive: boolean
  video?: {
    vimeoId?: string
    vimeoUrl?: string
  }
}

interface SkillCategoryOption {
  value: string
  label: string
}

const defaultSkillCategoryOptions: SkillCategoryOption[] = [
  { value: 'trading_basics', label: 'Trading Basics' },
  { value: 'risk_management', label: 'Risk Management' },
  { value: 'trading_psychology', label: 'Trading Psychology' },
  { value: 'technical_analysis', label: 'Technical Analysis' },
  { value: 'choosing_broker', label: 'Choosing Broker' },
]

export default function LessonsPage() {
  const queryClient = useQueryClient()
  const [selectedLevel, setSelectedLevel] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null)
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedSkillCategories, setSelectedSkillCategories] = useState<string[]>([])
  
  // Загружаем настройки скилов
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.get<{ data: { settings: any } }>('/admin/settings')
      return res.data.data.settings
    },
  })

  // Получаем список скилов из настроек или используем дефолтные
  const skillCategoryOptions: SkillCategoryOption[] = settings?.skillCategories?.categories || defaultSkillCategoryOptions

  // Сбрасываем выбранные скилы при открытии модалки
  useEffect(() => {
    if (isModalOpen) {
      if (editingLesson) {
        // Используем skillCategories если есть, иначе берем skillCategory
        const categories = editingLesson.skillCategories?.length 
          ? editingLesson.skillCategories 
          : (editingLesson.skillCategory ? [editingLesson.skillCategory] : [])
        setSelectedSkillCategories(categories)
      } else {
        setSelectedSkillCategories([])
      }
    }
  }, [isModalOpen, editingLesson])
  
  const { data: levels } = useQuery({
    queryKey: ['levels'],
    queryFn: async () => {
      const res = await api.get<{ data: { levels: Level[] } }>('/admin/levels')
      return res.data.data.levels
    },
  })
  
  const { data: lessons, isLoading } = useQuery({
    queryKey: ['lessons', selectedLevel],
    queryFn: async () => {
      const params = selectedLevel ? `?levelId=${selectedLevel}` : ''
      const res = await api.get<{ data: { lessons: Lesson[] } }>(`/admin/lessons${params}`)
      return res.data.data.lessons
    },
  })
  
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await api.post('/admin/lessons', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] })
      queryClient.invalidateQueries({ queryKey: ['levels'] })
      toast.success('Урок создан')
      setIsModalOpen(false)
      setUploadedImage(null)
    },
    onError: () => {
      toast.error('Ошибка создания урока')
    },
  })
  
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await api.patch(`/admin/lessons/${id}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] })
      toast.success('Урок обновлён')
      setEditingLesson(null)
      setIsModalOpen(false)
      setUploadedImage(null)
    },
    onError: () => {
      toast.error('Ошибка обновления урока')
    },
  })
  
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/lessons/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] })
      queryClient.invalidateQueries({ queryKey: ['levels'] })
      toast.success('Урок удалён')
    },
  })
  
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    
    const data = {
      levelId: formData.get('levelId') as string,
      type: formData.get('type') as string,
      title: formData.get('title') as string,
      description: formData.get('description') as string || '',
      image: uploadedImage || (formData.get('image') as string) || '',
      content: formData.get('content') as string || '',
      xpReward: parseInt(formData.get('xpReward') as string) || 15,
      skillCategory: selectedSkillCategories[0] || 'trading_basics',
      skillCategories: selectedSkillCategories,
      skillProgress: parseInt(formData.get('skillProgress') as string) || 5,
      order: parseInt(formData.get('order') as string) || 0,
      pointsReward: parseInt(formData.get('pointsReward') as string) || 10,
      durationMinutes: parseInt(formData.get('durationMinutes') as string) || 10,
      isActive: formData.get('isActive') === 'on',
      video: {
        vimeoId: formData.get('vimeoId') as string || '',
        vimeoUrl: formData.get('vimeoUrl') as string || '',
      },
    }
    
    if (editingLesson) {
      updateMutation.mutate({ id: editingLesson._id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const toggleSkillCategory = (value: string) => {
    setSelectedSkillCategories(prev => 
      prev.includes(value) 
        ? prev.filter(v => v !== value)
        : [...prev, value]
    )
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение')
      return
    }

    const formData = new FormData()
    formData.append('file', file)

    setIsUploading(true)
    try {
      const res = await api.post<{ data: { url: string } }>('/admin/uploads/single?folder=images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      setUploadedImage(res.data.data.url)
      toast.success('Изображение загружено')
    } catch (error) {
      toast.error('Ошибка загрузки изображения')
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveImage = () => {
    setUploadedImage(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const getLevelDisplay = (level: Level) => {
    return level.title || `Level ${level.number}`
  }
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Уроки</h1>
        <button
          onClick={() => {
            setEditingLesson(null)
            setUploadedImage(null)
            setIsModalOpen(true)
          }}
          className="btn-primary"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Добавить урок
        </button>
      </div>
      
      {/* Filter */}
      <div className="card">
        <select
          className="w-64"
          value={selectedLevel}
          onChange={(e) => setSelectedLevel(e.target.value)}
        >
          <option value="">Все уровни</option>
          {levels?.map((level) => (
            <option key={level._id} value={level._id}>
              Уровень {level.number}: {getLevelDisplay(level)}
            </option>
          ))}
        </select>
      </div>
      
      {/* Table */}
      <div className="card overflow-hidden p-0">
        <table className="table">
          <thead>
            <tr>
              <th>№</th>
              <th>Картинка</th>
              <th>Название</th>
              <th>Уровень</th>
              <th>XP / Skill</th>
              <th>Категория</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-400">
                  Загрузка...
                </td>
              </tr>
            ) : lessons?.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-400">
                  Уроки не найдены
                </td>
              </tr>
            ) : (
              lessons?.map((lesson) => {
                // Получаем категории для отображения
                const displayCategories = lesson.skillCategories?.length 
                  ? lesson.skillCategories 
                  : (lesson.skillCategory ? [lesson.skillCategory] : [])
                
                return (
                <tr key={lesson._id}>
                  <td className="text-white font-medium">{lesson.order}</td>
                  <td>
                    {lesson.image ? (
                      <img 
                        src={getImageUrl(lesson.image)} 
                        alt="" 
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center">
                        <PhotoIcon className="w-6 h-6 text-gray-500" />
                      </div>
                    )}
                  </td>
                  <td>
                    <div>
                      <p className="font-medium text-white">{lesson.title}</p>
                    </div>
                  </td>
                  <td className="text-gray-300">
                    {lesson.levelId?.number || '-'}
                  </td>
                  <td>
                    <div className="text-sm">
                      <p className="text-blue-400">+{lesson.xpReward || 15} XP</p>
                      <p className="text-green-400">+{lesson.skillProgress || 5}%</p>
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {displayCategories.map((cat, idx) => (
                        <span key={idx} className="badge-blue text-xs">
                          {skillCategoryOptions.find(s => s.value === cat)?.label || cat}
                        </span>
                      ))}
                      {displayCategories.length === 0 && (
                        <span className="badge-gray text-xs">Не указано</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={lesson.isActive ? 'badge-green' : 'badge-gray'}>
                      {lesson.isActive ? 'Активен' : 'Скрыт'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          setEditingLesson(lesson)
                          setUploadedImage(null)
                          setIsModalOpen(true)
                        }}
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Удалить урок?')) {
                            deleteMutation.mutate(lesson._id)
                          }
                        }}
                        className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )})
            )}
          </tbody>
        </table>
      </div>
      
      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">
                {editingLesson ? 'Редактировать урок' : 'Новый урок'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Основные настройки */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Уровень *
                  </label>
                  <select
                    name="levelId"
                    required
                    defaultValue={typeof editingLesson?.levelId === 'object' ? editingLesson.levelId._id : ''}
                    className="w-full"
                  >
                    <option value="">Выберите уровень</option>
                    {levels?.map((level) => (
                      <option key={level._id} value={level._id}>
                        {level.number}. {getLevelDisplay(level)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Тип *
                  </label>
                  <select
                    name="type"
                    required
                    defaultValue={editingLesson?.type || 'article'}
                    className="w-full"
                  >
                    <option value="video">Видео</option>
                    <option value="article">Статья</option>
                    <option value="quiz">Квиз</option>
                    <option value="practice">Практика</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Порядок
                  </label>
                  <input
                    type="number"
                    name="order"
                    min="0"
                    defaultValue={editingLesson?.order || 0}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Название */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  required
                  defaultValue={editingLesson?.title}
                  placeholder="Welcome to Trading"
                  className="w-full"
                />
              </div>

              {/* Описание */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  name="description"
                  defaultValue={editingLesson?.description}
                  placeholder="Your first step into the world of trading"
                  className="w-full"
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Картинка урока
                </label>
                
                {/* Показываем превью если есть картинка */}
                {(uploadedImage || editingLesson?.image) && (
                  <div className="relative mb-3 inline-block">
                    <img 
                      src={uploadedImage ? getImageUrl(uploadedImage) : getImageUrl(editingLesson?.image)} 
                      alt="Preview" 
                      className="h-32 rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Кнопка загрузки */}
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="lesson-image-upload"
                  />
                  <label
                    htmlFor="lesson-image-upload"
                    className={`btn-secondary cursor-pointer ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <PhotoIcon className="w-5 h-5 mr-2" />
                    {isUploading ? 'Загрузка...' : (uploadedImage || editingLesson?.image ? 'Изменить картинку' : 'Загрузить картинку')}
                  </label>
                </div>

                {/* Скрытое поле для сохранения URL */}
                <input
                  type="hidden"
                  name="image"
                  value={uploadedImage || editingLesson?.image || ''}
                />
              </div>

              {/* Video (Vimeo) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Vimeo ID (для видео-уроков)
                  </label>
                  <input
                    type="text"
                    name="vimeoId"
                    defaultValue={editingLesson?.video?.vimeoId}
                    placeholder="123456789"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Vimeo URL
                  </label>
                  <input
                    type="url"
                    name="vimeoUrl"
                    defaultValue={editingLesson?.video?.vimeoUrl}
                    placeholder="https://vimeo.com/123456789"
                    className="w-full"
                  />
                </div>
              </div>

              {/* Content (Markdown) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Content - Markdown
                </label>
                <textarea
                  name="content"
                  rows={10}
                  defaultValue={editingLesson?.content}
                  placeholder="# Welcome to Trading&#10;&#10;Trading is the art and science of buying and selling..."
                  className="w-full font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Поддерживается Markdown: # заголовок, **жирный**, *курсив*, - списки
                </p>
              </div>

              {/* XP and Skill settings */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-white mb-4">Награды и прогресс</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      XP Reward (Trader Level)
                    </label>
                    <input
                      type="number"
                      name="xpReward"
                      min="0"
                      defaultValue={editingLesson?.xpReward || 15}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Очки для повышения уровня трейдера
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Skill Categories (множественный выбор)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {skillCategoryOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleSkillCategory(opt.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            selectedSkillCategories.includes(opt.value)
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Выберите одну или несколько категорий навыков
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Skill Progress (%)
                    </label>
                    <input
                      type="number"
                      name="skillProgress"
                      min="1"
                      max="100"
                      defaultValue={editingLesson?.skillProgress || 5}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Процент прогресса в категории
                    </p>
                  </div>
                </div>
              </div>

              {/* Legacy points */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Points Reward (устаревшее)
                  </label>
                  <input
                    type="number"
                    name="pointsReward"
                    min="0"
                    defaultValue={editingLesson?.pointsReward || 10}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Длительность (минут)
                  </label>
                  <input
                    type="number"
                    name="durationMinutes"
                    min="1"
                    defaultValue={editingLesson?.durationMinutes || 10}
                    className="w-full"
                  />
                </div>
              </div>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isActive"
                  defaultChecked={editingLesson?.isActive ?? true}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-gray-300">Активен (виден пользователям)</span>
              </label>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false)
                    setEditingLesson(null)
                  }}
                  className="btn-secondary"
                >
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  {editingLesson ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
